import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { format, toZonedTime } from 'date-fns-tz';
import DotIcon from '../../assets/images/DotIcon.svg';
import { removeNote, setNotes } from '../../features/notes/noteSlice';
import CloseIcon from '../../assets/images/closeIcon.svg';
import RightArrowIcon from '../../assets/images/righArrowIcon.svg';
import DeleteIcon from '../../assets/images/DeleteIcon.svg';
import { useDeleteNoteMutation, useGetNotesQuery } from '../../api/note';
import {
    goTOPageHref,
    handlePageChange,
    removeNoteIcon,
} from '../../utils/readiumUtils';
import { Modal } from 'antd';

interface Properties {
    top: number;
    left: number;
}
interface Note {
    id: number;
    book_id: number;
    text: string;
    cfi: string;
    page_no: number;
    href: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    properties: Properties;
    chapter_name: string;
}
interface NotesLibraryModalProps {
    onClose: () => void;
    bookId: string;
    isNotesLibraryModalOpen: boolean;
    offset: number;
}

const NotesLibraryModal: React.FC<NotesLibraryModalProps> = ({
    onClose,
    bookId,
    isNotesLibraryModalOpen,
    offset,
}) => {
    const dispatch = useDispatch();
    const {
        data: notes = [],
        isLoading,
        isError,
        refetch,
    } = useGetNotesQuery(bookId);

    dispatch(setNotes(notes?.data));

    const [deleteNote, { isSuccess: isDeleteSuccess }] =
        useDeleteNoteMutation();
    const confirmDelete = (id: number) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this note?',
            content: 'This action cannot be undone.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: () => handleDelete(id),
        });
    };
    const handleDelete = async (id: number) => {
        try {
            dispatch(removeNote(id));
            await deleteNote({ id: id, bookId: bookId });
            removeNoteIcon(window, id);
        } catch (error) {
            console.error('Failed to delete note:', error);
        }
    };

    const handleRightArrowClick = (href: string) => {
        goTOPageHref(window, href);
        handlePageChange(window)
    };
    useEffect(() => {
        refetch();
    }, [isDeleteSuccess, refetch, isNotesLibraryModalOpen]);
    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-[60rem] h-[30rem] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center sticky top-0 bg-white z-10  border-gray-200 pb-2">
                    <h2 className="text-xl font-bold">Notes Library</h2>
                    <button
                        data-testid="notes-library-close-button"
                        className="text-gray-500 hover:text-gray-700"
                        onClick={onClose}
                    >
                        <img src={CloseIcon} alt="Close" />
                    </button>
                </div>

                {/* Content */}
                <div className="overflow-y-auto flex-1 mt-4 custom-scrollbar">
                    {notes?.data?.length > 0 ? (
                        <ul className="space-y-4">
                            {notes?.data.map((note: Note, index: number) => (
                                <li
                                    key={note.id}
                                    onClick={() => {
                                        handleRightArrowClick(note.href);
                                        handlePageChange(window);
                                        onClose();
                                    }}
                                    className="flex items-center justify-between p-4 rounded-xl border border-gray-300 shadow-sm hover:bg-[#F7F9FD] hover:cursor-pointer"
                                >
                                    <div className="flex items-center space-x-4">
                                        <div className="flex items-center justify-center w-12 h-10 bg-green-100 rounded-lg ">
                                            <span className="text-black font-bold text-lg">
                                                {index + 1}
                                            </span>
                                        </div>

                                        <div className="flex flex-col">
                                            <div>
                                                <h3 className="font-semibold text-black">
                                                    {note.text.length > 50
                                                        ? note.text.slice(
                                                            0,
                                                            50
                                                        ) + ' ...'
                                                        : note.text}
                                                </h3>
                                            </div>
                                            <div className="flex space-x-4">
                                                {note.chapter_name && (
                                                    <p className="text-sm text-gray-600 flex flex-row">
                                                        <div>
                                                            {note.chapter_name}
                                                        </div>
                                                    </p>
                                                )}
                                                <p className="text-sm text-gray-600 flex flex-row space-x-2">
                                                    {note.chapter_name && (
                                                        <img
                                                            src={DotIcon}
                                                            alt=""
                                                        />
                                                    )}
                                                    <div>
                                                        Page no:{' '}
                                                        {note.page_no -
                                                            offset <=
                                                            0
                                                            ? 1
                                                            : note.page_no -
                                                            offset}
                                                    </div>
                                                </p>
                                                <p className="text-sm text-gray-600 flex flex-row space-x-2">
                                                    <img src={DotIcon} alt="" />
                                                    <div className="text-nowrap">
                                                        {format(
                                                            toZonedTime(
                                                                new Date(
                                                                    note.created_at
                                                                ),
                                                                'Asia/Kolkata'
                                                            ),
                                                            'dd MMM yyyy, hh:mm a',
                                                            {
                                                                timeZone:
                                                                    'Asia/Kolkata',
                                                            }
                                                        )}
                                                    </div>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-12">
                                        <button
                                            data-testid="notes-library-item-delete-button"
                                            className="hover:bg-blue-100 rounded-full p-1"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                confirmDelete(note.id);
                                            }}
                                        >
                                            <img
                                                src={DeleteIcon}
                                                alt="Delete"
                                            />
                                        </button>
                                        <button
                                            className="text-gray-500 hover:text-gray-700"
                                            data-testid="notes-library-item-goto-button"
                                        >
                                            <img
                                                src={RightArrowIcon}
                                                alt="Arrow"
                                            />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-gray-500 text-center">
                            No notes saved.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotesLibraryModal;
